/**
 * Operations Projects Page
 * Two-panel layout: project list sidebar + detail panel
 */

import { useState, useCallback } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ProjectStatsBar } from '../components/ProjectStatsBar';
import { ProjectFilters } from '../components/ProjectFilters';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectDetailPanel } from '../components/ProjectDetailPanel';
import { ProjectFormModal } from '../components/ProjectFormModal';
import {
  useProjects,
  useProject,
  useProjectsStats,
  useCreateProject,
  useUpdateProject,
  useArchiveProject,
  useUnarchiveProject,
  useCompleteWorkflowStep,
  useUncompleteMilestone,
} from '../hooks/useProjects';
import { useOperationsPermissions } from '@/features/operations-settings';
import type { ProjectsQueryParams, CreateProjectInput, UpdateProjectInput } from '../types';

export function ProjectsPage() {
  // State
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<string | undefined>(undefined);
  const [params, setParams] = useState<ProjectsQueryParams>({
    pageNumber: 0, // 0-indexed to match backend DAO
    pageLength: 50,
    sortKey: 'scheduled_date',
    sortDirection: 1,
  });

  // Queries
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    isError: isProjectsError,
    error: projectsError,
  } = useProjects(params);
  const { data: selectedProject, isLoading: isLoadingProject } = useProject(selectedProjectId);
  const { data: stats, isLoading: isLoadingStats } = useProjectsStats({ type: params.type });

  // Mutations
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const archiveProject = useArchiveProject();
  const unarchiveProject = useUnarchiveProject();
  const completeStep = useCompleteWorkflowStep();
  const uncompleteStep = useUncompleteMilestone();

  // Permissions
  const { canCreateProjects, canEditProjects, canDeleteProjects } = useOperationsPermissions();

  // Handlers
  const handleParamsChange = useCallback((newParams: Partial<ProjectsQueryParams>) => {
    setParams((prev: ProjectsQueryParams) => ({ ...prev, ...newParams }));
  }, []);

  const handleProjectSelect = useCallback((projectId: string) => {
    setSelectedProjectId(projectId);
  }, []);

  const handleNewProject = useCallback(() => {
    setEditingProject(undefined);
    setIsFormOpen(true);
  }, []);

  const handleEditProject = useCallback(() => {
    if (selectedProjectId) {
      setEditingProject(selectedProjectId);
      setIsFormOpen(true);
    }
  }, [selectedProjectId]);

  const handleFormSubmit = useCallback(
    async (data: CreateProjectInput | UpdateProjectInput) => {
      try {
        if (editingProject) {
          await updateProject.mutateAsync({ id: editingProject, input: data as UpdateProjectInput });
          toast.success('Project updated successfully');
        } else {
          const created = await createProject.mutateAsync(data as CreateProjectInput);
          setSelectedProjectId(created.id);
          toast.success('Project created successfully');
        }
        setIsFormOpen(false);
        setEditingProject(undefined);
      } catch {
        toast.error(editingProject ? 'Failed to update project' : 'Failed to create project');
      }
    },
    [editingProject, createProject, updateProject]
  );

  const handleArchive = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      await archiveProject.mutateAsync(selectedProjectId);
      toast.success('Project archived');
      setSelectedProjectId(undefined);
    } catch {
      toast.error('Failed to archive project');
    }
  }, [selectedProjectId, archiveProject]);

  const handleUnarchive = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      await unarchiveProject.mutateAsync(selectedProjectId);
      toast.success('Project unarchived');
    } catch {
      toast.error('Failed to unarchive project');
    }
  }, [selectedProjectId, unarchiveProject]);

  const handleCompleteStep = useCallback(
    async (stepKey: string) => {
      if (!selectedProjectId) return;
      try {
        await completeStep.mutateAsync({ id: selectedProjectId, stepKey });
        toast.success('Step completed');
      } catch {
        toast.error('Failed to complete step');
      }
    },
    [selectedProjectId, completeStep]
  );

  const handleUncompleteStep = useCallback(
    async (stepKey: string) => {
      if (!selectedProjectId) return;
      try {
        await uncompleteStep.mutateAsync({ projectId: selectedProjectId, milestoneKey: stepKey });
        toast.success('Step reverted');
      } catch {
        toast.error('Failed to revert step');
      }
    },
    [selectedProjectId, uncompleteStep]
  );

  const projects = projectsData?.projects || [];

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 90px)' }}>
      {/* Stats Bar */}
      <div className="p-4 border-b">
        <ProjectStatsBar stats={stats} isLoading={isLoadingStats} />
      </div>

      {/* Action Bar */}
      <div className="p-4 border-b flex items-center gap-4">
        {canCreateProjects && (
          <Button onClick={handleNewProject}>
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        )}
        <div className="flex-1">
          <ProjectFilters params={params} onParamsChange={handleParamsChange} />
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden mt-0">
        {/* Project List Sidebar */}
        <div className="w-80 border-r flex flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {isProjectsError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>
                    {projectsError instanceof Error ? projectsError.message : 'Failed to load projects'}
                  </AlertDescription>
                </Alert>
              ) : isLoadingProjects ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                ))
              ) : projects.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No projects found
                </div>
              ) : (
                projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    isSelected={project.id === selectedProjectId}
                    onClick={() => handleProjectSelect(project.id)}
                  />
                ))
              )}
            </div>
          </ScrollArea>

          {projectsData && !isProjectsError && (
            <div className="p-3 border-t text-sm text-muted-foreground text-center">
              {projectsData.total} project
              {projectsData.total !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="flex-1 overflow-hidden">
          <ProjectDetailPanel
            project={selectedProject}
            isLoading={isLoadingProject && !!selectedProjectId}
            onEdit={handleEditProject}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onCompleteStep={handleCompleteStep}
            onUncompleteStep={handleUncompleteStep}
            isArchiving={archiveProject.isPending}
            isUnarchiving={unarchiveProject.isPending}
            isCompletingStep={completeStep.isPending || uncompleteStep.isPending}
            canEditProjects={canEditProjects}
            canDeleteProjects={canDeleteProjects}
          />
        </div>
      </div>

      {/* Form Modal */}
      <ProjectFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        project={editingProject ? selectedProject : undefined}
        onSubmit={handleFormSubmit}
        isSubmitting={createProject.isPending || updateProject.isPending}
      />
    </div>
  );
}
